import pick from 'lodash/pick'
import { dequal } from 'dequal'
import { logError } from '@sequencehq/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api/utils'
import { useNotifications } from '@sequencehq/core-components'
import { TaxCategory } from '../taxRates.types'

export type UpdateTaxCategoryInput = Pick<TaxCategory, 'name' | 'description'>

export function useUpdateTaxCategory() {
  const { displayNotification } = useNotifications()

  const { mutateAsync: postNewTaxCategoryVersion, reset } = useMutation(
    dashboardv99990101Client.postNewTaxCategoryVersion,
    {
      onError: (err: any) => {
        logError(err)
      }
    }
  )

  const updateTaxCategory = async (
    taxCategory: TaxCategory,
    taxCategoryInput: UpdateTaxCategoryInput
  ): Promise<TaxCategory | null | undefined> => {
    return await postNewTaxCategoryVersion({
      id: taxCategory.id,
      body: {
        version: taxCategory.version + 1,
        name: taxCategoryInput.name,
        description: taxCategoryInput.description,
        isPublished: true
      }
    })
  }

  return async (
    initialTaxCategory: TaxCategory,
    taxCategoryInput: UpdateTaxCategoryInput
  ) => {
    const wasTaxCategoryModified = !dequal(
      pick(initialTaxCategory, ['name', 'description']),
      pick(taxCategoryInput, ['name', 'description'])
    )

    let taxCategory: TaxCategory | null | undefined = initialTaxCategory

    if (wasTaxCategoryModified) {
      taxCategory = await updateTaxCategory(taxCategory, taxCategoryInput)
    }

    // If tax category is undefined, updateTaxCategory failed and we should not proceed
    if (!taxCategory) {
      displayNotification('Failed to update tax category', {
        type: 'error'
      })

      return
    }

    displayNotification('Tax category updated', {
      type: 'success'
    })

    reset()
  }
}
